/**
 * @fileoverview Match3 Game Hook
 * @description Core game logic for the Match3 puzzle game
 */

import { useState, useCallback, useRef, useEffect } from "react";
import {
  Match3Tile,
  Match3GameState,
  Match3Result,
  Match3GameConfig,
  Match3TimeBonusConfig,
  GemType,
  SpecialType,
  GridPosition,
  MatchResult,
  ALL_GEM_TYPES,
  DEFAULT_MATCH3_GAME_CONFIG,
} from "../types";

/** Grid size (8x8) */
const GRID_SIZE = 8;

/** Animation delays in ms */
const MATCH_DELAY = 300;
const CASCADE_DELAY = 200;
const SWAP_DELAY = 150;

/**
 * Generate a unique ID for tiles
 */
const generateTileId = (): string => {
  return `tile-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Get a random gem type
 */
const getRandomGemType = (): GemType => {
  return ALL_GEM_TYPES[Math.floor(Math.random() * ALL_GEM_TYPES.length)];
};

/**
 * Create a new tile at position
 */
const createTile = (row: number, col: number, gemType?: GemType): Match3Tile => ({
  id: generateTileId(),
  row,
  col,
  gemType: gemType || getRandomGemType(),
  specialType: "none",
  isSelected: false,
  isRemoving: false,
  isFalling: false,
  isNew: true,
});

/**
 * Initialize grid without initial matches
 */
const initializeGrid = (): Match3Tile[][] => {
  const grid: Match3Tile[][] = [];
  
  for (let row = 0; row < GRID_SIZE; row++) {
    grid[row] = [];
    for (let col = 0; col < GRID_SIZE; col++) {
      let gemType = getRandomGemType();
      
      // Avoid creating initial matches
      let attempts = 0;
      while (attempts < 10) {
        // Check horizontal match
        if (col >= 2) {
          if (grid[row][col - 1].gemType === gemType && grid[row][col - 2].gemType === gemType) {
            gemType = getRandomGemType();
            attempts++;
            continue;
          }
        }
        // Check vertical match
        if (row >= 2) {
          if (grid[row - 1][col].gemType === gemType && grid[row - 2][col].gemType === gemType) {
            gemType = getRandomGemType();
            attempts++;
            continue;
          }
        }
        break;
      }
      
      grid[row][col] = createTile(row, col, gemType);
      grid[row][col].isNew = false; // Initial tiles aren't "new"
    }
  }
  
  return grid;
};

/**
 * Check if two positions are adjacent
 */
const areAdjacent = (pos1: GridPosition, pos2: GridPosition): boolean => {
  const rowDiff = Math.abs(pos1.row - pos2.row);
  const colDiff = Math.abs(pos1.col - pos2.col);
  return (rowDiff === 1 && colDiff === 0) || (rowDiff === 0 && colDiff === 1);
};

/**
 * Find all matches in the grid
 */
const findMatches = (grid: Match3Tile[][]): MatchResult[] => {
  const matches: MatchResult[] = [];
  const processed = new Set<string>();
  
  // Check horizontal matches
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE - 2; col++) {
      const gemType = grid[row][col].gemType;
      const matchedPositions: GridPosition[] = [{ row, col }];
      
      let nextCol = col + 1;
      while (nextCol < GRID_SIZE && grid[row][nextCol].gemType === gemType) {
        matchedPositions.push({ row, col: nextCol });
        nextCol++;
      }
      
      if (matchedPositions.length >= 3) {
        const key = matchedPositions.map(p => `${p.row},${p.col}`).sort().join("|");
        if (!processed.has(key)) {
          processed.add(key);
          
          let matchType: MatchResult["matchType"] = "three";
          let points = 30;
          let createsSpecial: SpecialType = "none";
          
          if (matchedPositions.length === 4) {
            matchType = "four";
            points = 60;
            createsSpecial = "bomb";
          } else if (matchedPositions.length >= 5) {
            matchType = "five";
            points = 150;
            createsSpecial = "lightning";
          }
          
          matches.push({
            matchedTiles: matchedPositions,
            matchType,
            points,
            createsSpecial,
            specialPosition: createsSpecial !== "none" ? matchedPositions[Math.floor(matchedPositions.length / 2)] : undefined,
          });
        }
        col = nextCol - 1; // Skip processed tiles
      }
    }
  }
  
  // Check vertical matches
  for (let col = 0; col < GRID_SIZE; col++) {
    for (let row = 0; row < GRID_SIZE - 2; row++) {
      const gemType = grid[row][col].gemType;
      const matchedPositions: GridPosition[] = [{ row, col }];
      
      let nextRow = row + 1;
      while (nextRow < GRID_SIZE && grid[nextRow][col].gemType === gemType) {
        matchedPositions.push({ row: nextRow, col });
        nextRow++;
      }
      
      if (matchedPositions.length >= 3) {
        const key = matchedPositions.map(p => `${p.row},${p.col}`).sort().join("|");
        if (!processed.has(key)) {
          processed.add(key);
          
          let matchType: MatchResult["matchType"] = "three";
          let points = 30;
          let createsSpecial: SpecialType = "none";
          
          if (matchedPositions.length === 4) {
            matchType = "four";
            points = 60;
            createsSpecial = "bomb";
          } else if (matchedPositions.length >= 5) {
            matchType = "five";
            points = 150;
            createsSpecial = "lightning";
          }
          
          matches.push({
            matchedTiles: matchedPositions,
            matchType,
            points,
            createsSpecial,
            specialPosition: createsSpecial !== "none" ? matchedPositions[Math.floor(matchedPositions.length / 2)] : undefined,
          });
        }
        row = nextRow - 1; // Skip processed tiles
      }
    }
  }
  
  // Check for L and T shapes (intersecting matches)
  // This is a simplified version - we detect when horizontal and vertical matches share tiles
  const allMatchedPositions = new Map<string, { matches: number; positions: GridPosition[] }>();
  
  matches.forEach(match => {
    match.matchedTiles.forEach(pos => {
      const key = `${pos.row},${pos.col}`;
      const existing = allMatchedPositions.get(key);
      if (existing) {
        existing.matches++;
        existing.positions.push(pos);
      } else {
        allMatchedPositions.set(key, { matches: 1, positions: [pos] });
      }
    });
  });
  
  // Find intersection points (L/T shapes)
  allMatchedPositions.forEach((data, key) => {
    if (data.matches >= 2) {
      // This tile is part of multiple matches - it's an L or T shape
      const [row, col] = key.split(",").map(Number);
      matches.push({
        matchedTiles: [{ row, col }],
        matchType: "l_shape",
        points: 50, // Bonus for L/T shape intersection
        createsSpecial: "rainbow",
        specialPosition: { row, col },
      });
    }
  });
  
  return matches;
};

/**
 * Apply bomb special effect (3x3 area)
 */
const applyBombEffect = (grid: Match3Tile[][], row: number, col: number): GridPosition[] => {
  const affected: GridPosition[] = [];
  for (let r = Math.max(0, row - 1); r <= Math.min(GRID_SIZE - 1, row + 1); r++) {
    for (let c = Math.max(0, col - 1); c <= Math.min(GRID_SIZE - 1, col + 1); c++) {
      affected.push({ row: r, col: c });
    }
  }
  return affected;
};

/**
 * Apply lightning special effect (entire row + column)
 */
const applyLightningEffect = (grid: Match3Tile[][], row: number, col: number): GridPosition[] => {
  const affected: GridPosition[] = [];
  // Entire row
  for (let c = 0; c < GRID_SIZE; c++) {
    affected.push({ row, col: c });
  }
  // Entire column (avoiding duplicate)
  for (let r = 0; r < GRID_SIZE; r++) {
    if (r !== row) {
      affected.push({ row: r, col });
    }
  }
  return affected;
};

/**
 * Apply rainbow special effect (all gems of one color)
 */
const applyRainbowEffect = (grid: Match3Tile[][], targetGemType: GemType): GridPosition[] => {
  const affected: GridPosition[] = [];
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      if (grid[row][col].gemType === targetGemType) {
        affected.push({ row, col });
      }
    }
  }
  return affected;
};

interface UseMatch3Options {
  config?: Match3GameConfig;
  onScoreChange?: (score: number) => void;
  onTimeBonus?: (bonus: Match3TimeBonusConfig) => void;
  onGameOver?: (result: Match3Result) => void;
  onCombo?: (level: number) => void;
}

/**
 * Main Match3 game hook
 */
export const useMatch3 = (options: UseMatch3Options = {}) => {
  const { 
    config = DEFAULT_MATCH3_GAME_CONFIG, 
    onScoreChange, 
    onTimeBonus, 
    onGameOver,
    onCombo,
  } = options;

  // Game state
  const [gameState, setGameState] = useState<Match3GameState>({
    grid: [],
    score: 0,
    timeRemaining: config.initial_time_seconds,
    initialTime: config.initial_time_seconds,
    comboLevel: 0,
    movesMade: 0,
    isPlaying: false,
    isPaused: false,
    isGameOver: false,
    isProcessing: false,
    selectedTile: null,
    lastMatch: null,
    startedAt: null,
    endedAt: null,
    timeBonusesEarned: 0,
    powerUpsUsed: 0,
  });

  // Track achieved time bonuses
  const achievedBonusesRef = useRef<Set<number>>(new Set());
  const maxComboRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * Start a new game
   */
  const startGame = useCallback(() => {
    const newGrid = initializeGrid();
    achievedBonusesRef.current = new Set();
    maxComboRef.current = 0;

    setGameState({
      grid: newGrid,
      score: 0,
      timeRemaining: config.initial_time_seconds,
      initialTime: config.initial_time_seconds,
      comboLevel: 0,
      movesMade: 0,
      isPlaying: true,
      isPaused: false,
      isGameOver: false,
      isProcessing: false,
      selectedTile: null,
      lastMatch: null,
      startedAt: new Date().toISOString(),
      endedAt: null,
      timeBonusesEarned: 0,
      powerUpsUsed: 0,
    });
  }, [config.initial_time_seconds]);

  /**
   * Timer effect
   */
  useEffect(() => {
    if (gameState.isPlaying && !gameState.isPaused && !gameState.isGameOver) {
      timerRef.current = setInterval(() => {
        setGameState(prev => {
          if (prev.timeRemaining <= 1) {
            // Game over
            clearInterval(timerRef.current!);
            const result: Match3Result = {
              score: prev.score,
              timePlayedSeconds: prev.initialTime + prev.timeBonusesEarned,
              movesMade: prev.movesMade,
              maxCombo: maxComboRef.current,
              powerUpsUsed: prev.powerUpsUsed,
              timeBonusesEarned: prev.timeBonusesEarned,
              pointsEarned: 0, // Calculated by parent
              isNewHighScore: false, // Set by parent
              isNewFamilyRecord: false, // Set by parent
              milestonesAchieved: [],
            };
            onGameOver?.(result);
            return {
              ...prev,
              timeRemaining: 0,
              isPlaying: false,
              isGameOver: true,
              endedAt: new Date().toISOString(),
            };
          }
          return { ...prev, timeRemaining: prev.timeRemaining - 1 };
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [gameState.isPlaying, gameState.isPaused, gameState.isGameOver, onGameOver]);

  /**
   * Check and apply time bonuses
   */
  const checkTimeBonuses = useCallback((newScore: number) => {
    config.time_bonuses.forEach(bonus => {
      if (newScore >= bonus.score_threshold && !achievedBonusesRef.current.has(bonus.score_threshold)) {
        achievedBonusesRef.current.add(bonus.score_threshold);
        setGameState(prev => ({
          ...prev,
          timeRemaining: prev.timeRemaining + bonus.bonus_seconds,
          timeBonusesEarned: prev.timeBonusesEarned + bonus.bonus_seconds,
        }));
        onTimeBonus?.(bonus);
      }
    });
  }, [config.time_bonuses, onTimeBonus]);

  /**
   * Ref to update grid state during animations
   */
  const updateGridRef = useRef<(grid: Match3Tile[][]) => void>(() => {});
  
  // Update ref when setGameState changes
  useEffect(() => {
    updateGridRef.current = (grid: Match3Tile[][]) => {
      setGameState(prev => ({ ...prev, grid }));
    };
  }, []);

  /**
   * Process matches and cascades
   */
  const processMatches = useCallback(async (grid: Match3Tile[][]): Promise<{ newGrid: Match3Tile[][]; totalPoints: number; cascadeLevel: number }> => {
    let currentGrid = grid.map(row => row.map(tile => ({ ...tile })));
    let totalPoints = 0;
    let cascadeLevel = 0;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const matches = findMatches(currentGrid);
      if (matches.length === 0) break;

      cascadeLevel++;
      const comboMultiplier = Math.pow(config.scoring.combo_multiplier, cascadeLevel - 1);

      // Calculate points for this cascade
      let cascadePoints = 0;
      const tilesToRemove = new Set<string>();
      const specialsToCreate: { pos: GridPosition; type: SpecialType }[] = [];

      matches.forEach(match => {
        cascadePoints += Math.round(match.points * comboMultiplier);
        match.matchedTiles.forEach(pos => {
          tilesToRemove.add(`${pos.row},${pos.col}`);
          
          // Check for special tile activation
          const tile = currentGrid[pos.row][pos.col];
          if (tile.specialType !== "none") {
            let affectedTiles: GridPosition[] = [];
            switch (tile.specialType) {
              case "bomb":
                affectedTiles = applyBombEffect(currentGrid, pos.row, pos.col);
                break;
              case "lightning":
                affectedTiles = applyLightningEffect(currentGrid, pos.row, pos.col);
                break;
              case "rainbow":
                // Rainbow needs a target gem type - use the most common one in remaining tiles
                const gemCounts = new Map<GemType, number>();
                for (let r = 0; r < GRID_SIZE; r++) {
                  for (let c = 0; c < GRID_SIZE; c++) {
                    const g = currentGrid[r][c].gemType;
                    gemCounts.set(g, (gemCounts.get(g) || 0) + 1);
                  }
                }
                let targetGem: GemType = "red";
                let maxCount = 0;
                gemCounts.forEach((count, gem) => {
                  if (count > maxCount) {
                    maxCount = count;
                    targetGem = gem;
                  }
                });
                affectedTiles = applyRainbowEffect(currentGrid, targetGem);
                break;
            }
            affectedTiles.forEach(p => tilesToRemove.add(`${p.row},${p.col}`));
            cascadePoints += 50; // Bonus for special activation
          }
        });

        // Track specials to create
        if (match.createsSpecial !== "none" && match.specialPosition) {
          specialsToCreate.push({ pos: match.specialPosition, type: match.createsSpecial });
        }
      });

      totalPoints += cascadePoints;

      // Mark tiles as removing and UPDATE STATE to trigger animations
      tilesToRemove.forEach(key => {
        const [row, col] = key.split(",").map(Number);
        currentGrid[row][col].isRemoving = true;
      });

      // Update state to show explosion animations
      updateGridRef.current(currentGrid.map(row => row.map(tile => ({ ...tile }))));

      // Wait for removal animation
      await new Promise(resolve => setTimeout(resolve, MATCH_DELAY));

      // Now replace the removed tiles with new ones
      tilesToRemove.forEach(key => {
        const [row, col] = key.split(",").map(Number);
        currentGrid[row][col] = createTile(row, col);
      });

      // Make tiles fall down
      for (let col = 0; col < GRID_SIZE; col++) {
        let emptyRow = GRID_SIZE - 1;
        for (let row = GRID_SIZE - 1; row >= 0; row--) {
          const tile = currentGrid[row][col];
          // Skip tiles that were just created (they'll be replaced)
          if (!tilesToRemove.has(`${row},${col}`)) {
            if (row !== emptyRow) {
              // Move tile down
              currentGrid[emptyRow][col] = { ...tile, row: emptyRow, isFalling: true };
              currentGrid[row][col] = createTile(row, col);
              currentGrid[row][col].isNew = true;
            }
            emptyRow--;
          }
        }
        // Fill empty spaces at top
        for (let row = emptyRow; row >= 0; row--) {
          currentGrid[row][col] = createTile(row, col);
          currentGrid[row][col].isNew = true;
        }
      }

      // Create special tiles
      specialsToCreate.forEach(({ pos, type }) => {
        if (currentGrid[pos.row] && currentGrid[pos.row][pos.col]) {
          currentGrid[pos.row][pos.col].specialType = type;
        }
      });

      // Update state to show falling animations
      updateGridRef.current(currentGrid.map(row => row.map(tile => ({ ...tile }))));

      // Wait for cascade animation
      await new Promise(resolve => setTimeout(resolve, CASCADE_DELAY));

      // Reset animation flags for next iteration
      currentGrid = currentGrid.map(row =>
        row.map(tile => ({
          ...tile,
          isRemoving: false,
          isFalling: false,
          isNew: false,
        }))
      );
    }

    return { newGrid: currentGrid, totalPoints, cascadeLevel };
  }, [config.scoring.combo_multiplier]);

  /**
   * Handle tile selection/swap
   */
  const selectTile = useCallback(async (row: number, col: number) => {
    if (gameState.isProcessing || gameState.isGameOver || gameState.isPaused || !gameState.isPlaying) {
      return;
    }

    const { selectedTile, grid } = gameState;

    // If no tile selected, select this one
    if (!selectedTile) {
      setGameState(prev => {
        const newGrid = prev.grid.map(r => r.map(t => ({ ...t, isSelected: false })));
        newGrid[row][col].isSelected = true;
        return { ...prev, grid: newGrid, selectedTile: { row, col } };
      });
      return;
    }

    // If same tile clicked, deselect
    if (selectedTile.row === row && selectedTile.col === col) {
      setGameState(prev => {
        const newGrid = prev.grid.map(r => r.map(t => ({ ...t, isSelected: false })));
        return { ...prev, grid: newGrid, selectedTile: null };
      });
      return;
    }

    // If not adjacent, select new tile instead
    if (!areAdjacent(selectedTile, { row, col })) {
      setGameState(prev => {
        const newGrid = prev.grid.map(r => r.map(t => ({ ...t, isSelected: false })));
        newGrid[row][col].isSelected = true;
        return { ...prev, grid: newGrid, selectedTile: { row, col } };
      });
      return;
    }

    // Try to swap
    setGameState(prev => ({ ...prev, isProcessing: true, selectedTile: null }));

    // Create swapped grid
    const newGrid = grid.map(r => r.map(t => ({ ...t, isSelected: false })));
    const tile1 = { ...newGrid[selectedTile.row][selectedTile.col] };
    const tile2 = { ...newGrid[row][col] };
    
    // Swap gems
    newGrid[selectedTile.row][selectedTile.col] = { ...tile2, row: selectedTile.row, col: selectedTile.col };
    newGrid[row][col] = { ...tile1, row, col };

    // Check if swap creates matches
    const matches = findMatches(newGrid);
    
    if (matches.length === 0) {
      // Invalid swap - revert
      await new Promise(resolve => setTimeout(resolve, SWAP_DELAY));
      setGameState(prev => ({ ...prev, isProcessing: false }));
      return;
    }

    // Valid swap - process matches
    setGameState(prev => ({
      ...prev,
      grid: newGrid,
      movesMade: prev.movesMade + 1,
    }));

    await new Promise(resolve => setTimeout(resolve, SWAP_DELAY));

    // Process all matches and cascades
    const { newGrid: finalGrid, totalPoints, cascadeLevel } = await processMatches(newGrid);

    // Update max combo
    if (cascadeLevel > maxComboRef.current) {
      maxComboRef.current = cascadeLevel;
    }

    // Update state
    setGameState(prev => {
      const newScore = prev.score + totalPoints;
      onScoreChange?.(newScore);
      if (cascadeLevel > 1) {
        onCombo?.(cascadeLevel);
      }
      
      // Check time bonuses
      setTimeout(() => checkTimeBonuses(newScore), 0);

      return {
        ...prev,
        grid: finalGrid,
        score: newScore,
        comboLevel: cascadeLevel,
        isProcessing: false,
        powerUpsUsed: prev.powerUpsUsed + (matches.some(m => m.createsSpecial !== "none") ? 1 : 0),
      };
    });
  }, [gameState, processMatches, onScoreChange, onCombo, checkTimeBonuses]);

  /**
   * Pause game
   */
  const pauseGame = useCallback(() => {
    setGameState(prev => ({ ...prev, isPaused: true }));
  }, []);

  /**
   * Resume game
   */
  const resumeGame = useCallback(() => {
    setGameState(prev => ({ ...prev, isPaused: false }));
  }, []);

  /**
   * End game early
   */
  const endGame = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    setGameState(prev => {
      const result: Match3Result = {
        score: prev.score,
        timePlayedSeconds: prev.initialTime + prev.timeBonusesEarned - prev.timeRemaining,
        movesMade: prev.movesMade,
        maxCombo: maxComboRef.current,
        powerUpsUsed: prev.powerUpsUsed,
        timeBonusesEarned: prev.timeBonusesEarned,
        pointsEarned: 0,
        isNewHighScore: false,
        isNewFamilyRecord: false,
        milestonesAchieved: [],
      };
      onGameOver?.(result);
      
      return {
        ...prev,
        isPlaying: false,
        isGameOver: true,
        endedAt: new Date().toISOString(),
      };
    });
  }, [onGameOver]);

  return {
    // State
    gameState,
    grid: gameState.grid,
    score: gameState.score,
    timeRemaining: gameState.timeRemaining,
    comboLevel: gameState.comboLevel,
    isPlaying: gameState.isPlaying,
    isPaused: gameState.isPaused,
    isGameOver: gameState.isGameOver,
    isProcessing: gameState.isProcessing,
    selectedTile: gameState.selectedTile,
    
    // Actions
    startGame,
    selectTile,
    pauseGame,
    resumeGame,
    endGame,
  };
};

export default useMatch3;

